import { useEffect, useState, useMemo } from 'react';

import {
  Building2Icon,
  PlusIcon,
  MicIcon,
  ExternalLinkIcon,
  ReplyIcon,
  ImageIcon,
  CirclePlayIcon,
  BookIcon,
  PhoneIcon,
  SmartphoneIcon,
} from 'lucide-react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { WhatsAppPhonePreview } from '@/components/atoms/WhatsAppPhonePreview';
import { cn } from '@/lib/utils';
import { getComponentsFromTemplate } from '@/modules/whatsapp/utils';
import { selectCampaignId } from '@/redux/session/selectors';
import { WhatsappTemplate } from '@/shared/types/broadcast';
import { BaseCampaignSender } from '@/shared/types/campaigns';
import { Integration, IntegrationType } from '@/shared/types/integration';
import { markdownToHtml } from '@/util/markdown';

import { useCampaignSubscribers } from '../../hooks/useCampaignSubscribers';
import { CampaignForm, getPhoneFromIntegrationName, isVoiceIntegrationType } from '../../utils';
import { resolveVariables, resolveWhatsAppParameters } from '../../utils/utils';

interface Props {
  className?: string;
  integrations?: Array<Partial<Integration>>;
  whatsappTemplatesMap?: Record<string, WhatsappTemplate[]>;
  activeSender?: 'primary' | 'not_delivered_fallback' | number;
  primarySender?: Partial<BaseCampaignSender>;
  fallbackSender?: Partial<BaseCampaignSender>;
  noResponseSenders?: Partial<BaseCampaignSender>[];
}

/**
 * Mobile message preview component that displays WhatsApp or SMS interfaces
 * @param className - Optional CSS class name
 * @param integrations - Available integrations for phone number extraction
 * @param whatsappTemplatesMap - Map of integration ID to WhatsApp templates
 * @param activeSender - Which sender is currently active ('primary', 'not_delivered_fallback', or number for no-response index)
 * @param primarySender - Primary sender data
 * @param fallbackSender - Fallback if not delivered sender data (index 0)
 * @param noResponseSenders - Array of fallback if no response senders (index 1+)
 */
export function MessagePreview({
  className,
  integrations = [],
  whatsappTemplatesMap = {},
  activeSender = 'primary',
  primarySender: propPrimarySender,
  fallbackSender: propFallbackSender,
  noResponseSenders: propNoResponseSenders,
}: Props) {
  const { control } = useFormContext<CampaignForm>();
  const { t } = useTranslation();

  // Get campaign ID from Redux for fetching subscribers
  const campaignId = useSelector(selectCampaignId);

  // Fetch first subscriber for variable resolution
  const { campaign_subscribers } = useCampaignSubscribers(campaignId);
  const firstSubscriber = campaign_subscribers?.[0];

  // Use props if provided, otherwise fall back to form context
  const formPrimarySender = useWatch({ control, name: 'primarySender' });
  const formFallbackSenders = useWatch({ control, name: 'fallbackSenders' });
  const formFallbackSender = formFallbackSenders?.[0];
  const formNoResponseSenders = formFallbackSenders?.slice(1);

  const primarySender = propPrimarySender || formPrimarySender;
  const fallbackSender = propFallbackSender || formFallbackSender;
  const noResponseSenders = propNoResponseSenders || formNoResponseSenders;

  // Determine which sender to show based on activeSender
  // activeSender can be 'primary', 'not_delivered_fallback' (for not-delivered), or a number (for no-response at index 1+)
  const currentSender = (() => {
    if (activeSender === 'not_delivered_fallback' && fallbackSender) {
      return fallbackSender;
    }
    if (typeof activeSender === 'number' && noResponseSenders) {
      // activeSender is the actual index in fallbackSenders array (1+)
      // noResponseSenders is already sliced from index 1, so we need to adjust
      const noResponseIndex = activeSender - 1;
      if (noResponseSenders[noResponseIndex]) {
        return noResponseSenders[noResponseIndex];
      }
    }
    return primarySender;
  })();

  // Find the selected template for preview (used in renderTemplateContent)
  // Look up templates by the current sender's integration ID
  const currentSenderTemplates = currentSender?.integrationId
    ? whatsappTemplatesMap[currentSender.integrationId] || []
    : [];
  const currentTemplate = currentSenderTemplates.find((t) => t.id === currentSender?.template?.id);

  /**
   * Extract email address from integration name (format: "email · test-email-dev@moveo.dev")
   * @param integrationName - The integration name string
   * @returns The extracted email address or null if not found
   */
  const getEmailFromIntegrationName = (integrationName?: string) => {
    if (!integrationName) {
      return null;
    }
    // Match email after the · separator or directly in the string
    const emailMatch = integrationName.match(/[\w.-]+@[\w.-]+\.\w+/);
    if (!emailMatch) {
      return null;
    }
    return emailMatch[0];
  };

  // Function to get header media info for rendering separately
  const getHeaderMediaInfo = (sender: typeof currentSender) => {
    if (sender?.integrationType === 'whatsapp' && sender?.template?.id) {
      if (!currentTemplate) {
        return null;
      }
      const { header } = getComponentsFromTemplate(currentTemplate);

      // Check if header has media format (not TEXT)
      if (header && header.format && header.format.toLowerCase() !== 'text') {
        // Extract example URL from header_handle if available
        let exampleUrl: string | undefined;
        if (header.example?.header_handle && Array.isArray(header.example.header_handle)) {
          exampleUrl = header.example.header_handle[0];
        }

        return {
          format: header.format.toLowerCase() as 'image' | 'video' | 'document',
          exampleUrl,
        };
      }
    }
    return null;
  };

  // Function to render template content with example values (without buttons)
  const renderTemplateContent = (sender: typeof currentSender) => {
    if (sender?.integrationType === 'whatsapp' && sender?.template?.id) {
      // Use the currentTemplate we already found

      if (!currentTemplate) {
        return null;
      }

      const { header, body, footer } = getComponentsFromTemplate(currentTemplate);

      let fullContent = '';

      // Add text header if exists - resolve WhatsApp parameters
      // Media headers are rendered separately
      if (header?.text) {
        const resolvedHeader = resolveWhatsAppParameters(
          header.text,
          sender.headerParameters || [],
          firstSubscriber?.context
        );
        fullContent += `<strong>${resolvedHeader}</strong>\n\n`;
      }

      // Add body - resolve WhatsApp parameters
      if (body?.text) {
        const resolvedBody = resolveWhatsAppParameters(
          body.text,
          sender.bodyParameters || [],
          firstSubscriber?.context
        );
        fullContent += resolvedBody;
      }

      // Add footer - resolve WhatsApp parameters
      if (footer?.text) {
        const resolvedFooter = resolveWhatsAppParameters(
          footer.text,
          sender.footerParameters || [],
          firstSubscriber?.context
        );
        fullContent += `\n\n${resolvedFooter}`;
      }

      return fullContent;
    } else if (
      sender?.integrationType === IntegrationType.INFOBIP_SMS ||
      sender?.integrationType === IntegrationType.INFOBIP_RCS ||
      sender?.integrationType === IntegrationType.MSTAT_VIBER ||
      sender?.integrationType === IntegrationType.EMAIL ||
      sender?.integrationType === IntegrationType.TWILIO_VOICE ||
      sender?.integrationType === IntegrationType.TELNYX_VOICE ||
      sender?.integrationType === IntegrationType.AGORA_VOICE
    ) {
      return sender.content;
    }
    return '';
  };

  // Function to get template buttons for rendering separately
  const getTemplateButtons = (sender: typeof currentSender) => {
    if (sender?.integrationType === 'whatsapp' && sender?.template?.id) {
      const template = currentTemplate;
      if (!template) {
        return [];
      }
      const { buttons } = getComponentsFromTemplate(template);
      return buttons || [];
    }
    return [];
  };

  const previewContent = renderTemplateContent(currentSender);
  const templateButtons = getTemplateButtons(currentSender);
  const headerMedia = getHeaderMediaInfo(currentSender);

  // Resolve variables with first subscriber's context
  const resolvedContent = useMemo(
    () => resolveVariables(previewContent || '', firstSubscriber?.context),
    [previewContent, firstSubscriber]
  );

  // Get the actual integration to extract phone number or email
  const currentIntegration = integrations.find(
    (integration) => integration.integration_id === currentSender?.integrationId
  );
  const phoneNumber = getPhoneFromIntegrationName(currentIntegration?.name);
  const emailAddress = getEmailFromIntegrationName(currentIntegration?.name);

  const isWhatsApp = currentSender?.integrationType === IntegrationType.WHATSAPP;
  const isSMS =
    currentSender?.integrationType === IntegrationType.INFOBIP_SMS ||
    currentSender?.integrationType === IntegrationType.INFOBIP_RCS ||
    currentSender?.integrationType === IntegrationType.MSTAT_VIBER;
  const isEmail = currentSender?.integrationType === IntegrationType.EMAIL;
  const isVoice = isVoiceIntegrationType(currentSender?.integrationType);

  // State to store HTML-converted content for email type
  const [htmlContent, setHtmlContent] = useState<string>('');

  // Convert markdown to HTML for email content
  useEffect(() => {
    if (isEmail && resolvedContent) {
      // For email, convert markdown to HTML
      markdownToHtml(resolvedContent)
        .then((html) => setHtmlContent(html))
        .catch((error) => {
          console.error('Failed to convert markdown to HTML:', error);
          setHtmlContent(resolvedContent); // Fallback to raw content
        });
    } else {
      // For WhatsApp and SMS, use content as-is
      setHtmlContent(resolvedContent || '');
    }
  }, [resolvedContent, isEmail]);

  // Use the converted HTML content (markdownToHtml already sanitizes with rehype-sanitize)
  const sanitizedContent = htmlContent;

  // Platform-specific styles using cn utility
  const getHeaderClasses = () =>
    cn('flex items-center gap-3 px-4 py-3', {
      'bg-[#F2EEEA]': isWhatsApp,
      'border-b bg-background': isSMS || isEmail || isVoice,
    });

  const getAvatarClasses = () =>
    cn('flex h-8 w-8 items-center justify-center rounded-full', { 'bg-background': isWhatsApp });

  // Only the WhatsApp header sits on fixed light `bg-[#F2EEEA]`, so only its text is pinned.
  const getHeaderTitleClasses = () =>
    cn('truncate text-sm font-semibold', isWhatsApp ? 'text-[#020617]' : 'text-foreground');

  const getHeaderSubtitleClasses = () =>
    cn('text-xs', isWhatsApp ? 'text-[#48576C]' : 'text-foreground-muted');

  const getMessageBubbleClasses = () =>
    cn('max-w-60 rounded-lg p-2', {
      'bg-background': isWhatsApp,
      'bg-bg-muted': isSMS,
      //make only top left and bottom right rounded
      'rounded-none rounded-tl-lg rounded-tr-lg': templateButtons.length > 0,
    });

  // Function to render WhatsApp template buttons
  const renderWhatsAppButton = (
    button: { type: 'QUICK_REPLY' | 'URL' | 'PHONE_NUMBER'; text: string; url?: string },
    index: number,
    isLastButton: boolean
  ) => {
    const getButtonIcon = () => {
      switch (button.type) {
        case 'URL':
          return <ExternalLinkIcon className="h-4 w-4 text-[#4FB5E9]" />;
        case 'PHONE_NUMBER':
          return <PhoneIcon className="h-4 w-4 text-[#4FB5E9]" />;
        case 'QUICK_REPLY':
        default:
          return <ReplyIcon className="h-4 w-4 text-[#4FB5E9]" />;
      }
    };

    return (
      <button
        key={index}
        className={cn(
          'flex w-full items-center justify-center gap-2 border-t border-gray-300 bg-background px-4 py-3 text-sm text-blue-600 transition-colors hover:bg-muted',
          { 'rounded-br-lg rounded-bl-lg': isLastButton }
        )}
        disabled
      >
        {getButtonIcon()}
        <span className="font-medium text-[#4FB5E9]">{button.text}</span>
      </button>
    );
  };

  // Function to render media header
  const renderMediaHeader = (mediaInfo: {
    format: 'image' | 'video' | 'document';
    exampleUrl?: string;
  }) => {
    // If we have an example URL, show the actual media
    if (mediaInfo.exampleUrl) {
      switch (mediaInfo.format) {
        case 'image':
          return (
            <div className="mb-2 overflow-hidden rounded-t">
              <img
                src={mediaInfo.exampleUrl}
                alt=""
                className="h-auto max-h-[200px] w-full object-cover"
                onError={(e) => {
                  // Fallback to icon if image fails to load
                  e.currentTarget.style.display = 'none';
                  const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                  if (fallback) {
                    fallback.style.display = 'flex';
                  }
                }}
              />
              <div className="mb-2 hidden items-center gap-2 rounded bg-muted p-2">
                <ImageIcon size={16} className="text-foreground-muted" />
                <span className="text-sm text-foreground-muted">image.jpg</span>
              </div>
            </div>
          );
        case 'video':
          return (
            <div className="mb-2 overflow-hidden rounded-t">
              <video
                src={mediaInfo.exampleUrl}
                controls
                className="h-auto max-h-[200px] w-full"
                onError={(e) => {
                  // Fallback to icon if video fails to load
                  e.currentTarget.style.display = 'none';
                  const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                  if (fallback) {
                    fallback.style.display = 'flex';
                  }
                }}
              />
              <div className="mb-2 hidden items-center gap-2 rounded bg-muted p-2">
                <CirclePlayIcon size={16} className="text-foreground-muted" />
                <span className="text-sm text-foreground-muted">video.mp4</span>
              </div>
            </div>
          );
        case 'document':
          // For documents, show icon with filename from URL
          return (
            <div className="mb-2 flex items-center gap-2 rounded bg-muted p-2">
              <BookIcon size={16} className="text-foreground-muted" />
              <span className="text-sm text-foreground-muted">document.pdf</span>
            </div>
          );
      }
    }

    return null;
  };

  // No sender selected yet — show the empty mobile-preview state
  if (!isWhatsApp && !isSMS && !isEmail && !isVoice) {
    return (
      <div className={cn('flex justify-center pt-7 pb-8', className)}>
        <div className="flex h-150 w-80 flex-col items-center justify-center gap-2 rounded-[32px] border border-stroke bg-background px-8 text-center shadow-md">
          <SmartphoneIcon className="mb-1 size-8 text-foreground-muted" />
          <p className="text-base font-semibold text-foreground-muted">
            {t('campaigns.build.preview_empty_title')}
          </p>
          <p className="text-sm text-foreground-muted">
            {t('campaigns.build.preview_empty_subtitle')}
          </p>
        </div>
      </div>
    );
  }

  // WhatsApp with a template — delegate to shared component
  if (isWhatsApp && currentTemplate) {
    return (
      <div className={cn('flex justify-center pt-7 pb-8', className)}>
        <WhatsAppPhonePreview
          template={currentTemplate}
          headerParameters={currentSender?.headerParameters}
          bodyParameters={currentSender?.bodyParameters}
          footerParameters={currentSender?.footerParameters}
          phoneNumber={phoneNumber}
          subscriberContext={firstSubscriber?.context}
        />
      </div>
    );
  }

  // SMS/Email/Voice preview
  return (
    <div className={cn('flex justify-center pt-7 pb-8', className)}>
      <div className="h-150 w-80 rounded-[32px] border border-stroke bg-background p-1.5 shadow-md">
        {/* Screen */}
        <div className="flex h-full w-full flex-col overflow-hidden rounded-[26px] bg-background">
          {/* Header */}
          <div className={getHeaderClasses()}>
            <div className={getAvatarClasses()}>
              <Building2Icon className="h-4 w-4 text-foreground-muted" />
            </div>
            <div className="min-w-0 flex-1">
              <div className={getHeaderTitleClasses()}>{isEmail ? emailAddress : phoneNumber}</div>
              <div className={getHeaderSubtitleClasses()}>{t('common.business_account')}</div>
            </div>
          </div>

          {/* Scrollable Content Area */}
          <div
            className="flex flex-1 flex-col overflow-y-auto"
            style={{
              backgroundImage: isWhatsApp
                ? 'url("/assets/campaigns/whatsapp_background.png")'
                : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          >
            <div className="p-4">
              {(sanitizedContent || headerMedia) && (
                <div className="flex flex-col">
                  <div className={getMessageBubbleClasses()}>
                    {/* Render media header if present */}
                    {headerMedia && renderMediaHeader(headerMedia)}
                    {/* Render text content */}
                    {sanitizedContent && (
                      <div
                        className={cn(
                          'text-sm [&_a]:text-accent [&_a]:underline [&_br]:my-2 [&_br]:block [&_em]:italic [&_h1]:my-2 [&_h1]:text-xl [&_h1]:font-bold [&_h2]:my-2 [&_h2]:text-lg [&_h2]:font-semibold [&_h3]:my-1 [&_h3]:text-base [&_h3]:font-semibold [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-2 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0 [&_strong]:font-semibold [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5',
                          { 'whitespace-pre-line': isWhatsApp }
                        )}
                        dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                      />
                    )}
                  </div>
                  {/* Render WhatsApp template buttons */}
                  {templateButtons.length > 0 && (
                    <div className="max-w-60 overflow-hidden rounded-br-lg rounded-bl-lg bg-background shadow-sm">
                      {templateButtons.map((button, index) =>
                        renderWhatsAppButton(button, index, index === templateButtons.length - 1)
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Input Bar */}
          <div className="flex h-20 items-start gap-2 bg-bg-muted px-3 py-2">
            <PlusIcon className="mt-1.5 h-5 w-5 text-foreground-muted" />
            <div className="flex h-8 w-full flex-1 items-center justify-end rounded-full border border-stroke bg-bg-muted pr-3">
              <MicIcon className="ml-4 h-5 w-5 text-foreground-muted" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
